import { useState } from 'react';
import dynamic from 'next/dynamic';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { addEmoji } from '@/utils/addEmoji';
import { useIsElementFocused } from '@/utils/hooks/utils/useIsElementFocused';
import { useValidateCaption } from '@/utils/hooks/utils/useValidateCaption';

import { CAPTION_TYPE, HASHTAG_LIST_TYPE } from '@/lib/captionsHashtagLists/types';
import PropTypes from 'prop-types';

import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import { Caption } from '@/components/composite/Caption';
import { Trans } from '@/components/tools/translation';
const EmojiPicker = dynamic(() => import('@/components/base/EmojiPicker'));

export const GenericCaption = ({
    captionLayoutConfig = { hasBorder: true, hasAvatar: true, label: null, aiButton: false },
    valid,
    caption,
    updateCaption,
    validateCaptionCallback,
    footerPortalContainer,
    initialRestrictions,
    inputProps,
    rows = 8,
    captionWrapperStyle = 'default',
    forcedSocialProfile = null,
    rootId, // wrapper element when the eventListener will be added to handle focus style of the caption
    captionBorderClassName
}) => {
    const { t } = useTranslation();
    const { activeSocialProfile } = useActiveSocialProfile();
    const [cursorPosition, setCursorPosition] = useState(0);
    const [captionRestrictions, setCaptionRestrictions] = useState(initialRestrictions);
    const { wrapperRef, isFocused, setIsFocused } = useIsElementFocused(rootId);

    useValidateCaption(
        caption,
        initialRestrictions,
        validateCaptionCallback,
        setCaptionRestrictions
    );

    return (
        <Caption captionWrapperStyle={captionWrapperStyle} className="space-y-4" ref={wrapperRef}>
            {captionLayoutConfig?.label ? captionLayoutConfig.label : null}
            <Caption.Content className="items-start">
                {captionLayoutConfig?.hasAvatar ? (
                    <Avatar
                        url={
                            (forcedSocialProfile ?? activeSocialProfile)?.external_api_data
                                ?.avatar ?? null
                        }
                        fallback={
                            (forcedSocialProfile ?? activeSocialProfile)?.slug?.charAt(0) ?? 'U'
                        }
                        profileType={
                            (forcedSocialProfile ?? activeSocialProfile)?.social_network_type
                        }
                        className="mr-5"
                    />
                ) : null}
                <Caption.TextArea
                    {...inputProps}
                    onBlur={(e) => setCursorPosition(e.target.selectionStart)}
                    value={caption}
                    onFocus={() => {
                        setIsFocused(true);
                    }}
                    onChange={(e) => {
                        updateCaption(e.target.value);
                    }}
                    rows={rows}
                    className="flex-1 leading-5"
                />
                {captionLayoutConfig?.hasBorder ? (
                    <Caption.Border
                        className={clsx(captionBorderClassName, {
                            'ring-icoGray-400 dark:ring-icoDarkMode-beton': !isFocused,
                            'ring-icoBlue': isFocused && valid?.valid,
                            'ring-icoYellow': isFocused && valid?.valid && valid?.warning,
                            'ring-icoRed': !valid?.valid
                        })}
                    />
                ) : null}
            </Caption.Content>
            <Caption.Footer className="!z-10 justify-end">
                <Caption.Restrictions
                    className="flex-1"
                    valid={valid}
                    content={
                        <>
                            {captionRestrictions?.map(({ type, restriction }, id) => {
                                return (
                                    <span key={type}>
                                        <Trans
                                            i18nKey={`commons:caption_restrictions.${type}`}
                                            values={{
                                                [type]: restriction.limit
                                            }}
                                            components={{
                                                tag0: <span className="font-GSemiBold" />
                                            }}
                                        />
                                        {id + 1 !== captionRestrictions.length ? ', ' : null}
                                    </span>
                                );
                            })}
                        </>
                    }
                />
                <div className="flex divide-x divide-icoGray-300 dark:divide-icoDarkMode-beton">
                    <div className="flex gap-5 pr-5">
                        <Caption.Popover
                            type={HASHTAG_LIST_TYPE}
                            currentCaption={caption}
                            updateCaption={updateCaption}
                            forcedSocialProfile={forcedSocialProfile}
                            buttonTitle={t('publishing:captions_and_lists.hashtag_list')}
                            portalContainer={footerPortalContainer}
                        />
                        <Caption.Popover
                            type={CAPTION_TYPE}
                            currentCaption={caption}
                            updateCaption={updateCaption}
                            forcedSocialProfile={forcedSocialProfile}
                            buttonTitle={t('publishing:captions_and_lists.popover.caption.title')}
                            portalContainer={footerPortalContainer}
                        />
                    </div>
                    <EmojiPicker
                        placement="bottom-end"
                        offset={[5, 10]}
                        selectEmoji={(emoji) =>
                            addEmoji(
                                emoji,
                                caption,
                                updateCaption,
                                cursorPosition,
                                setCursorPosition
                            )
                        }
                        className="ml-5 text-xl"
                        portalContainer={footerPortalContainer}
                    />
                </div>
            </Caption.Footer>
        </Caption>
    );
};

GenericCaption.propTypes = {
    captionLayoutConfig: PropTypes.object,
    valid: PropTypes.object,
    caption: PropTypes.string,
    updateCaption: PropTypes.func,
    validateCaptionCallback: PropTypes.func,
    initialRestrictions: PropTypes.arrayOf(PropTypes.object),
    inputProps: PropTypes.object
};
